import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import { API_URL, storedData, secret_key } from "./Constants";
import CryptoJS from "crypto-js";
import "./ReportForm.css"; // keep your authentic CSS intact
import { Chart, registerables } from 'chart.js';
import { Settings } from "lucide-react";
import html2pdf from "html2pdf.js";
Chart.register(...registerables);

export default function ReportForm() {
  const location = useLocation();
  const authKey = location.state?.auth;   // 🔐 Only source of secret key
  const { company_id: paramCompanyId, sibling: paramSibling , batch_id: paramBatchId} = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  // --- Decrypt user data ---
  let loggedUserData = null;
  if (storedData) {
    try {
      const bytes = CryptoJS.AES.decrypt(storedData, secret_key);
      loggedUserData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (err) {
      console.error("User data decryption error:", err);
    }
  }

  const company_id = paramCompanyId || loggedUserData?.company_id;
  const admission_no = paramSibling;
  const selectedBatch = paramBatchId; // You can implement batch selection logic as needed

 
// --- Fetch Report Data ---
useEffect(() => {
  if (!company_id || !admission_no) return;

  setLoading(true);

  axios
    .get(
      `${API_URL}/report_form/${company_id}/${admission_no}/${selectedBatch}`,
      {
        headers: {
                 Authorization: `CustomToken ${authKey}`,   // 🔐 Use passed key
        },
      }
    )
    .then((res) => {
      if (res.data.success) {
        setReport(res.data);
      } else {
        console.warn("Report returned success = false:", res.data);
      }
    })
    .catch((err) => {
      console.error("Error fetching report:", err);

      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        JSON.stringify(err?.response?.data);

      const status = err?.response?.status || "No status";

      alert(`API Error\nStatus: ${status}\nMessage: ${backendMessage}`);
    })
    .finally(() => setLoading(false));
}, [company_id, admission_no, selectedBatch]); // 🔐 Include authKey in dependencies


  // --- Download PDF ---

const downloadPDF = () => {
  if (!reportRef.current) return;

  setDownloading(true);

  const element = reportRef.current;

  // ✅ Hide button section
  const downloadSection = document.getElementById("downloadSection");
  if (downloadSection) downloadSection.style.display = "none";

  const opt = {
    margin: 5,
    filename: `ReportForm_${student.student_name || "Student"}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      scrollY: 0,
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    },
  };

  html2pdf()
    .set(opt)
    .from(element)
    .save()
    .finally(() => {
      // ✅ Restore button after PDF
      if (downloadSection) downloadSection.style.display = "block";
      setDownloading(false);
    });
};
  // */

// ✅ NEW (matches controller)
const student = report?.data?.student || {};
const subjects = report?.data?.subjects || [];
const rawScores = report?.data?.raw_scores || {};
const ranking = report?.data?.ranking || {};
const batch = report?.data?.batch || {};
const examTypes = report?.data?.exam_types || [];
const settings = report?.settings || {};
 // 🔹 Compute totals for examinable subjects
const examinableSubjects = subjects.filter(s => s.is_examinable == 1);

const examTotals = {};
examTypes.forEach(code => {
  examTotals[code] = 0;
});

let grandTotalMarks = 0;

examinableSubjects.forEach(subject => {
  const subjectRaw = rawScores[subject.subject_code] || {};

  examTypes.forEach(code => {
    const val = Number(subjectRaw[code]?.score || 0);
    examTotals[code] += val;
  });

  grandTotalMarks += Number(subject.marks || 0);
});

/* Compute Totals for Non-Examinable Subjects */

const nonExaminableSubjects = subjects.filter(s => s.is_examinable == 0);

const nonExamTotals = {};
examTypes.forEach(code => {
  nonExamTotals[code] = 0;
});

let nonExamGrandTotal = 0;

nonExaminableSubjects.forEach(subject => {
  const subjectRaw = rawScores[subject.subject_code] || {};

  examTypes.forEach(code => {
    const val = Number(subjectRaw[code]?.score || 0);
    nonExamTotals[code] += val;
  });

  nonExamGrandTotal += Number(subject.total_marks || 0);
});
const opt = {
  margin: 5,
  filename: `ReportForm_${student.student_name || "Student"}.pdf`,
  image: { type: "jpeg", quality: 0.98 },
  html2canvas: {
    scale: 2,
    useCORS: true,
    scrollY: 0,
    ignoreElements: (element) => {
      return element.classList?.contains("no-print");
    },
  },
  jsPDF: {
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  },
  pagebreak: {
    mode: ["avoid-all", "css", "legacy"],
  },
};

// Hook must run unconditionally
useEffect(() => {
  if (!student?.graph_data) return; // conditional logic inside

  const labels = JSON.parse(student.graph_data.terms || "[]");
  const points = JSON.parse(student.graph_data.points || "[]");

  const ctx = document.getElementById("studentPerformanceChart");
  if (!ctx) return;

  // Destroy previous chart if exists
  if (window.studentChartInstance) {
    window.studentChartInstance.destroy();
  }

  window.studentChartInstance = new Chart(ctx, {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Points",
          data: points,
          borderColor: "rgba(75,192,192,1)",
          backgroundColor: "rgba(75,192,192,0.2)",
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      scales: {
        y: { beginAtZero: true },
        x: { ticks: { autoSkip: false } },
      },
      plugins: { legend: { display: false } },
    },
  });
}, [student]); // dependency: student

// Early returns after hooks
if (loading)
  return <div className="loading text-center mt-5">Loading Report Form...</div>;
if (!report || !report.data?.student)
  return <div className="no-data text-center mt-5">No report found.</div>;


  return (
    
    <div className="container-fluid report-wrapper py-4">
      <div className="card shadow-lg p-4 report-container" ref={reportRef}>
        {/* ===== HEADER ===== */}
            {/* ===== MODERN HEADER ===== */}
<div className="report-header mb-4 p-3">

  {/* ===== TOP GRID ===== */}
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "90px 1fr 90px",
      alignItems: "center",
      gap: "10px",
    }}
  >
    {/* LEFT – LOGO */}
    <div>
      <img
        src={settings.logo_base64}
        alt="School Logo"
        style={{
          width: "85px",
          height: "85px",
          objectFit: "contain",
        }}
      />
    </div>

    {/* CENTER – SCHOOL DETAILS */}
    <div style={{ textAlign: "center", padding: "0 10px" }}>
      <h3 style={{ margin: 0, fontWeight: 700, fontSize: "1.5rem" }}>
        {settings.organization}
      </h3>

      <p style={{ margin: "2px 0", fontSize: "0.9rem", color: "#333" }}>
        {settings.address}
      </p>

      <p style={{ margin: "2px 0", fontSize: "0.9rem", color: "#333" }}>
        {settings.organization_email} • {settings.contacts}
      </p>

      {/* FULL ORIGINAL BLOCK PRESERVED */}
      <p
        style={{
          marginTop: "6px",
          fontSize: "0.8rem",
          fontStyle: "italic",
          color: "#555",
          lineHeight: "1.3",
        }}
      >
        <strong>Motto:</strong> {settings.slogan} &nbsp;|&nbsp;
        <strong>Vision:</strong> {settings.vision}
        <br />
        <strong>Mission:</strong> {settings.mission}
      </p>
    </div>

    {/* RIGHT – STUDENT PHOTO */}
    <div style={{ textAlign: "right" }}>
      {settings.student_photo && (
        <img
          src={settings.student_photo}
          alt="Student"
          style={{
            width: "85px",
            height: "85px",
            objectFit: "cover",
            borderRadius: "6px",
          }}
        />
      )}
    </div>
  </div>

  {/* ===== DIVIDER ===== */}
  <div
    style={{
      margin: "12px 0 8px",
      borderTop: "2px solid #444",
    }}
  />

  {/* ===== REPORT TITLE (FIXED) ===== */}
  <div
    style={{
      padding: "8px 10px",
      borderLeft: "4px solid #00796B",
      background: "#f8f9fa",
    }}
  >
    <h4
      style={{
        margin: 0,
        fontWeight: 700,
        textTransform: "uppercase",
        fontSize: "1.05rem",
        color: "#2c3e50",
        wordBreak: "break-word",   // ✅ prevents cutoff
        whiteSpace: "normal",      // ✅ ensures wrapping
      }}
    >
      {batch?.description}
    </h4>

    <div
      style={{
        marginTop: "3px",
        fontSize: "0.85rem",
        color: "#555",
      }}
    >
      Term {batch?.term} • {batch?.year}
    </div>
  </div>

</div>
        {/* ===== STUDENT INFO ===== */}
        <div className="report-table-scroll">
        <table className="report-table table table-bordered table-striped">
          <tbody>
            <tr>
              <td><strong>Student Name:</strong> {`${student.student_name || ""}`}</td>
              <td><strong>Admission No:</strong> {student.admission_no}</td>
              <td><strong>Class:</strong> {student.class_code}</td>
              <td>
                <strong>{student.is_cbc === 0 ? "KCPE:" : "KPSEA:"}</strong> {student.is_cbc === 0 ? student.kcpe : student.kpsea}
              </td>
            </tr>

            <tr>
              <td><strong>Parent Name:</strong> {`${student.father || ""} / ${student.mother || ""}`}</td>
              <td><strong>Phone No:</strong> {student.mobile_number}</td>
              {settings.show_position_in_report_form == 1 && (
                <>
                  <td>
                    <strong>Class Position:</strong>{" "}
                    {ranking.class_position && ranking.class_out_of
                      ? `${ranking.class_position} Out Of ${ranking.class_out_of}`
                      : "N/A"}
                  </td>

                  <td>
                    <strong>Stream Position:</strong>{" "}
                    {ranking.stream_position && ranking.stream_out_of
                      ? `${ranking.stream_position} Out Of ${ranking.stream_out_of}`
                      : "N/A"}
                  </td>
                </>
              )}
            </tr>
          </tbody>
        </table>
        </div>


{/* ===== EXAMINABLE SUBJECTS ===== */}
<div className="report-table-scroll">
  <table
  className="table table-bordered table-striped report-table"
  style={{
    fontSize: "0.9rem",
    minWidth: `${500 + examTypes.length * 70}px`
  }}
>
          <thead className="text-center" style={{ background: "#f3f4f6" }}>
            <tr>
              <th>SN</th>
              <th>Code</th>
              <th>Subject</th>
              {examTypes.map((code, idx) => (
                <th key={idx}>{code}</th>
              ))}
              <th>AVERAGE</th>
              <th>{settings.is_cbc === '1' ? "Level" : "Grade"}</th>
              <th>PTS</th>
              <th>Remarks</th>
              <th>Initials</th>
            </tr>
          </thead>

          <tbody>
          {subjects.filter(item => item.is_examinable == 1)
              .map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td className="text-left">{item.subject_code}</td>
                  <td className="text-left">{item.course_name}</td>
                  {examTypes.map((code, i) => {
                    const subjectRaw = rawScores[item.subject_code  ] || {};
                    const score = subjectRaw[code]?.score ?? "-";
                    return <td key={i}>{score}</td>;
                  })}

                  <td>{item.marks}</td>
                  <td>{item.grade}</td>
                  <td>{item.points}</td>
                  <td>{item.remarks}</td>
                  <td>{item.initials || "-"}</td>
                </tr>
              ))}

              {settings.hide_marks_in_report_form === '1' && examinableSubjects.length > 0 && (
  <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
    
    <td colSpan="3" style={{ textAlign: "right" }}>Total:</td>

    {examTypes.map((code, i) => (
      <td key={i} style={{ textAlign: "center" }}>
        {examTotals[code]}
      </td>
    ))}

    {examTypes.length > 1 && (
      <td style={{ textAlign: "center" }}>{grandTotalMarks}</td>
    )}

    <td>{ranking.mean_grade || ""}</td>
    <td style={{ textAlign: "center" }}>
      {Number(ranking.avg_points || 0).toFixed(2)}
    </td>

    <td></td>
    <td></td>
  </tr>
)}
          </tbody>
        </table>
      </div>


        {/* ===== PERFORMANCE SUMMARY ===== */}
        <div className="report-table-scroll">
          <table
            className="table table-sm table-bordered report-table"
            style={{
              width: "100%",
              textAlign: "center",
              fontSize: "0.95rem",
              borderColor: "#ddd"
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "blueviolet", color: "white" }}>
                <td colSpan={6} style={{ padding: "6px", fontWeight: "600" }}>
                  PERFORMANCE SUMMARY DETAILS
                </td>
              </tr>
            </thead>

            <tbody>
              {/* Row 1 */}
               {settings.hide_marks_in_report_form === '1' && (
              <tr style={{ backgroundColor: "#fafafa" }}>
                <td className="fw-bold">Subjects</td>
                <td>{ranking.total_subjects || "N/A"}</td>

                <td className="fw-bold">Marks</td>
                <td>{ranking.total_marks || "N/A"}</td>

                <td className="fw-bold">Out of</td>
                <td>{ranking.total_possible || "N/A"}</td>
              </tr>
              )}
              {/* Row 2 */}
              <tr style={{ backgroundColor: "#eef0f2" }}>
                <td colSpan={3}>
                  <strong>Average:</strong> {ranking.avg_points || "N/A"}
                </td>

                <td colSpan={3}>
                  <strong>Mean {settings.is_cbc === '1' ? "Level" : "Grade"}:</strong> {ranking.mean_grade || "N/A"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>


    {/* ===== PERFORMANCE GRAPH ===== */}
     {settings.is_cbc === '0' && (
    <div className="mt-4 text-center">
      <h5 className="fw-bold mb-2">Performance Trend</h5>

      <canvas
        id="studentPerformanceChart"
        style={{ width: "100%", maxWidth: "600px", height: "180px" }}
      ></canvas>

      <img
        id="studentPerformanceImage"
        alt="Performance Chart"
        style={{ display: "none", maxWidth: "100%", marginTop: "5px" }}
      />
    </div>
   )}

    {/* ===== NON-EXAMINABLE SUBJECTS ===== */}
    {settings.is_cbc === '1' && settings.has_madarasa === '1' && subjects.filter(item => item.is_examinable == 0).length > 0 && (
  <div className="report-table-scroll">
    <table className="table table-bordered table-striped table-sm report-table" style={{ fontSize: "0.9rem" }}>
      <thead className="text-center" style={{ background: "#f3f4f6" }}>
        <tr>
          <th>SN</th>
          <th>Code</th>
          <th>Subject</th>
          {examTypes.map((code, idx) => (
            <th key={idx}>{code}</th>
          ))}
          <th>Total</th>
          <th>Grade</th>
          <th>PTS</th>
          <th>Remarks</th>
          <th>Initials</th>
        </tr>
      </thead>

      <tbody>
    {subjects.filter(item => item.is_examinable == 0)
          .map((item, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td className="text-left">{item.subject_code}</td>
              <td className="text-left">{item.course_name}</td>

              {examTypes.map((code, i) => {
                const subjectRaw = rawScores[item.subject_code] || {};
                const score = subjectRaw[code]?.score ?? "-";
                return <td key={i}>{score}</td>;
              })}

              <td>{item.total_marks}</td>
              <td>{item.grade}</td>
              <td>{item.points}</td>
              <td>{item.remarks}</td>
              <td>{item.initials || "-"}</td>
            </tr>
          ))}
                  {/* Totals Section */}
                  {nonExaminableSubjects.length > 0 && (
          <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
            
            <td colSpan="3" style={{ textAlign: "right" }}>Total:</td>

            {examTypes.map((code, i) => (
              <td key={i} style={{ textAlign: "center" }}>
                {nonExamTotals[code]}
              </td>
            ))}

            <td style={{ textAlign: "center" }}>{nonExamGrandTotal}</td>

            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          )}
      </tbody>
    </table>
  </div>
)}

    {/* ===== KEY TABLE ===== */}
    {settings.is_cbc === '1' && (
    <div className="report-table-scroll">
    <table
      className="table table-striped report-table"
      style={{
        width: "100%",
        border: "1px solid #ccc",
        marginTop: "5px",
        textAlign: "center",
        fontSize: "0.9rem"
      }}
    >
      <thead>
        <tr style={{ backgroundColor: "blueviolet", color: "white" }}>
          <td colSpan="4" className="fw-bold">KEY</td>
        </tr>
        <tr style={{ backgroundColor: "#5c2ea3", color: "white" }}>
          <th>EE</th>
          <th>ME</th>
          <th>AE</th>
          <th>BE</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td style={{ backgroundColor: "#f7f7f7" }}>Exceeding Expectation</td>
          <td style={{ backgroundColor: "#f7f7f7" }}>Meeting Expectation</td>
          <td style={{ backgroundColor: "#f7f7f7" }}>Approaching Expectation</td>
          <td style={{ backgroundColor: "#f7f7f7" }}>Below Expectation</td>
        </tr>
      </tbody>
    </table>
    </div>
)}


{/* ===== REMARKS, FEES & SIGNATURE ===== */}
<div className="report-table-scroll">
  <table className="table table-bordered table-sm report-table" style={{ fontSize: "0.9rem" }}>
    <tbody>

      {/* CLASS TEACHER REMARKS */}
      <tr>
        <td colSpan={2} className="fw-bold">Class Teacher Remarks:</td>
        <td colSpan={4}><i>{ranking.teacher_remark ?? "Improve"}</i></td>
      </tr>

      {/* HEADTEACHER / PRINCIPAL */}
      <tr>
        <td colSpan={2} className="fw-bold">
          {settings.is_cbc === '1' ? "Head Teacher Remarks:" : "Principal Remarks:"}
        </td>
        <td colSpan={4}><i>{ranking.principal_remark ?? "Improve"}</i></td>
      </tr>

      {/* FEES & SIGNATURE */}
      <tr>
        <td colSpan={3} className="fw-bold">
          Fees Balance for {report.term}:{" "}
          <span style={{ color: "#d9534f" }}>{ranking.balance ?? "0.00"}</span>
        </td>

        <td colSpan={3}>
          <strong>
            {settings.is_cbc === '1' ? "Head Teacher Signature:" : "Principal Signature:"}
          </strong>
          <br />

          {settings.signature_base64 && (
            <img
              src={settings.signature_base64}
              alt="Signature"
              crossOrigin="anonymous"
              style={{
                maxWidth: "90px",
                maxHeight: "70px",
                objectFit: "contain",
                border: "1px solid #ccc",
                padding: "2px",
                marginRight: "5px"
              }}
            />
          )}

          {settings.stamp_base64 && (
            <img
              src={settings.stamp_base64}
              alt="Stamp"
              crossOrigin="anonymous"
              style={{
                maxWidth: "150px",
                maxHeight: "70px",
                objectFit: "contain",
                border: "1px solid #ccc",
                padding: "2px"
              }}
            />
          )}
        </td>
      </tr>

      {/* DATES */}
      {settings.hide_dates_in_report_form === 0 ? (
        <tr>
          <td colSpan={3} className="fw-bold">
            Closing Date:{" "}
            <span style={{ color: "#d9534f" }}>{settings.closing_date}</span>
          </td>

          <td colSpan={3} className="fw-bold">
            Opening Date:{" "}
            <span style={{ color: "#5cb85c" }}>{settings.opening_date}</span>
          </td>
        </tr>
      ) : (
        <tr><td colSpan={6}></td></tr>
      )}

    </tbody>
  </table>
</div>
      
      {/* ===== DOWNLOAD BUTTON ===== */}
        <div className="text-center mt-4" id="downloadSection">
          <button
            className="btn btn-primary px-4 py-2 rounded shadow-sm"
            onClick={downloadPDF}
            disabled={downloading}
          >
            {downloading ? "Generating PDF..." : "⬇️ Download PDF"}
          </button>
        </div>
</div>

    </div>
  );
}
